import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { applyForReferralSchema, referralCodeSchema } from "./referral-schema";

/** Public settings — used by the landing page + apply form (rates, thresholds, cookie window).
 *  The table is not anon-readable; we project only the safe marketing columns here. */
export const getReferralPublicSettings = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("referral_settings")
    .select(
      "tier_silver_bp, tier_gold_bp, tier_platinum_bp, gold_threshold_cents, platinum_threshold_cents, hold_days, min_payout_cents, payout_fee_cents, cookie_days, terms_version",
    )
    .eq("id", true)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ?? {
    tier_silver_bp: 1000,
    tier_gold_bp: 1250,
    tier_platinum_bp: 1500,
    gold_threshold_cents: 100000,
    platinum_threshold_cents: 1000000,
    hold_days: 14,
    min_payout_cents: 5000,
    payout_fee_cents: 200,
    cookie_days: 90,
    terms_version: 1,
  };
});

/** Get the currently-signed-in user's partner row (null if they never applied). */
export const getMyPartner = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as any;
    const { data, error } = await ctx.supabase
      .from("referral_partners")
      .select("*")
      .eq("user_id", ctx.userId)
      .maybeSingle();
    if (error && error.code !== "PGRST116") {
      console.error("getMyPartner", error);
      throw new Error("Unable to load partner profile.");
    }
    return { partner: data ?? null };
  });

/** Generate a code like FIXHEN-A7K2 — retries on collision. */
function generatePartnerCode(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no I/O/0/1 confusion
  let out = "";
  for (let i = 0; i < 4; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `FIXHEN-${out}`;
}

export const applyForReferral = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((raw: unknown) => applyForReferralSchema.parse(raw))
  .handler(async ({ context, data }) => {
    const ctx = context as any;
    const sb = ctx.supabase;

    // Enforce single-application server side (RLS is user-scoped, but a friendlier error helps).
    const existing = await sb
      .from("referral_partners")
      .select("id")
      .eq("user_id", ctx.userId)
      .maybeSingle();
    if (existing.data?.id) {
      throw new Error("You already have a partner profile.");
    }

    // Confirm the settings version they accepted matches (or is at least current).
    const { data: settings } = await sb
      .from("referral_settings")
      .select("terms_version")
      .eq("id", true)
      .maybeSingle();
    if ((settings?.terms_version ?? 1) !== data.accepted_terms_version) {
      throw new Error("Terms have been updated — please review them again.");
    }

    // Retry code generation on unique-violation (23505).
    const insertRow = {
      user_id: ctx.userId,
      display_name: data.display_name,
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      country: data.country,
      languages: data.languages,
      pitch: data.pitch || null,
      // Mirror pitch → audience_desc for backward-compat columns.
      audience_desc: data.pitch || "",
      promo_channels: data.promo_channels,
      payout_method: data.payout_method,
      payout_details: data.payout_details ?? {},
      accepted_terms_version: data.accepted_terms_version,
      accepted_terms_at: new Date().toISOString(),
    };


    let lastError: any = null;
    for (let attempt = 0; attempt < 6; attempt++) {
      const code = generatePartnerCode();
      const { data: created, error } = await sb
        .from("referral_partners")
        .insert({ ...insertRow, code })
        .select("*")
        .single();
      if (!error && created) {
        const { emitNotification, lookupUserEmail } = await import("@/lib/emails/emit.server");
        const email = ctx.claims?.email ?? (await lookupUserEmail(ctx.userId));
        await emitNotification("referral.applied", {
          email,
          customer_email: email,
          partner: {
            display_name: created.display_name,
            first_name: created.first_name,
            code: created.code,
            country: created.country,
            phone: created.phone,
            languages: created.languages,
            promo_channels: created.promo_channels,
            payout_method: created.payout_method,
            pitch: created.pitch,
          },
          admin_url: "https://fixhen.com/admin/referrals",
        });
        return { partner: created };
      }
      lastError = error;
      // 23505 unique_violation on either user_id or code
      if (error && error.code === "23505" && String(error.message).includes("code")) continue;
      break;
    }
    console.error("applyForReferral failed", lastError);
    throw new Error(
      lastError?.code === "23505"
        ? "You already have a partner profile."
        : "Unable to submit application. Please try again in a moment.",
    );
  });

/** Simple contact — user-side update of their own limited fields. */
export const updateMyPartnerSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((raw: unknown) =>
    z
      .object({
        display_name: z.string().trim().min(2).max(80).optional(),
        company: z.string().trim().max(120).nullable().optional(),
        country: z.string().trim().length(2).toUpperCase().optional(),
        website: z.string().trim().max(200).nullable().optional(),
        promo_channels: z.array(z.string()).max(16).optional(),
        payout_method: z.enum(["bank", "paypal", "wise", "revolut"]).optional(),
        payout_details: z.record(z.string()).optional(),
        tax_id: z.string().trim().max(40).nullable().optional(),
      })
      .parse(raw),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as any;
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) {
      if (v !== undefined) patch[k] = v;
    }
    if (Object.keys(patch).length === 0) return { ok: true };
    const { error } = await ctx.supabase
      .from("referral_partners")
      .update(patch)
      .eq("user_id", ctx.userId);
    if (error) {
      console.error("updateMyPartnerSettings", error);
      throw new Error("Unable to save changes.");
    }
    return { ok: true as const };
  });

/**
 * Server-side validation used by the tracker route to normalize a referral code before insert.
 * (Exported so the API route can share the schema.)
 */
export { referralCodeSchema };

/** Owner-driven referral code rename (validated + rate-limited in the database). */
export const updateMyReferralCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((raw: unknown) => z.object({ code: referralCodeSchema }).parse(raw))
  .handler(async ({ context, data }) => {
    const ctx = context as any;
    const { data: code, error } = await ctx.supabase.rpc("set_my_referral_code", {
      _code: data.code,
    });
    if (error) {
      const msg = String(error.message ?? "");
      throw new Error(
        /already taken/i.test(msg)
          ? "That code is already taken — try another one."
          : /7 days/i.test(msg)
            ? "You can change your code once every 7 days."
            : /reserved/i.test(msg)
              ? "That code is reserved."
              : /4-32|characters/i.test(msg)
                ? "Use 4–32 characters: A–Z, 0–9 and dashes (not at the start or end)."
                : "Unable to update your referral code.",
      );
    }
    return { code: code as string };
  });

/** Aggregate dashboard: partner + balance summary + settings + 30-day sparkline. */
export const getMyPartnerDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as any;
    const sb = ctx.supabase;

    const [partnerRes, settingsRes] = await Promise.all([
      sb.from("referral_partners").select("*").eq("user_id", ctx.userId).maybeSingle(),
      sb
        .from("referral_settings")
        .select(
          "tier_silver_bp, tier_gold_bp, tier_platinum_bp, gold_threshold_cents, platinum_threshold_cents, hold_days, min_payout_cents, payout_fee_cents, cookie_days, terms_version",
        )
        .eq("id", true)
        .maybeSingle(),
    ]);

    if (partnerRes.error && partnerRes.error.code !== "PGRST116") {
      throw new Error(partnerRes.error.message);
    }
    const partner = partnerRes.data ?? null;
    const settings = settingsRes.data ?? null;

    let summary = {
      pending_cents: 0,
      approved_cents: 0,
      paid_cents: 0,
      reversed_cents: 0,
      lifetime_subtotal_cents: 0,
    };
    let recentDaily: Array<{ day: string; conversions: number; commission_cents: number }> = [];

    if (partner) {
      const { data: sumData } = await sb.rpc("partner_balance_summary", { _partner_id: partner.id });
      if (Array.isArray(sumData) && sumData[0]) {
        const row = sumData[0] as any;
        summary = {
          pending_cents: Number(row.pending_cents ?? 0),
          approved_cents: Number(row.approved_cents ?? 0),
          paid_cents: Number(row.paid_cents ?? 0),
          reversed_cents: Number(row.reversed_cents ?? 0),
          lifetime_subtotal_cents: Number(row.lifetime_subtotal_cents ?? 0),
        };
      }

      const since = new Date();
      since.setUTCDate(since.getUTCDate() - 30);
      const { data: convs } = await sb
        .from("referral_conversions")
        .select("created_at, commission_cents, status")
        .eq("partner_id", partner.id)
        .gte("created_at", since.toISOString());

      const bucket = new Map<string, { conversions: number; commission_cents: number }>();
      for (let i = 0; i < 30; i++) {
        const d = new Date(since);
        d.setUTCDate(d.getUTCDate() + i);
        bucket.set(d.toISOString().slice(0, 10), { conversions: 0, commission_cents: 0 });
      }
      for (const c of convs ?? []) {
        if ((c as any).status === "reversed") continue;
        const key = new Date((c as any).created_at).toISOString().slice(0, 10);
        const b = bucket.get(key);
        if (b) {
          b.conversions += 1;
          b.commission_cents += Number((c as any).commission_cents ?? 0);
        }
      }
      recentDaily = Array.from(bucket.entries()).map(([day, v]) => ({ day, ...v }));
    }

    return { partner, settings, summary, recentDaily };
  });

/** Paginated conversions list for the signed-in partner. */
export const listMyConversions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((raw: unknown) =>
    z
      .object({
        status: z.enum(["pending", "approved", "paid", "reversed"]).optional(),
        limit: z.number().int().min(1).max(100).default(25),
        offset: z.number().int().min(0).default(0),
      })
      .parse(raw ?? {}),
  )
  .handler(async ({ context, data }) => {
    const ctx = context as any;
    const partner = await ctx.supabase
      .from("referral_partners")
      .select("id")
      .eq("user_id", ctx.userId)
      .maybeSingle();
    if (!partner.data) return { rows: [], total: 0 };

    let q = ctx.supabase
      .from("referral_conversions")
      .select(
        "id, created_at, code_snapshot, subtotal_cents, commission_rate_bp, commission_cents, status, hold_until, approved_at, reversed_reason, order_id",
        { count: "exact" },
      )
      .eq("partner_id", partner.data.id)
      .order("created_at", { ascending: false })
      .range(data.offset, data.offset + data.limit - 1);
    if (data.status) q = q.eq("status", data.status);
    const { data: rows, count, error } = await q;
    if (error) throw new Error(error.message);

    const orderIds = (rows ?? []).map((r: any) => r.order_id).filter(Boolean);
    let orderMap = new Map<string, string>();
    if (orderIds.length) {
      const { data: orders } = await ctx.supabase
        .from("orders")
        .select("id, order_number")
        .in("id", orderIds);
      for (const o of orders ?? []) orderMap.set((o as any).id, (o as any).order_number);
    }
    return {
      rows: (rows ?? []).map((r: any) => ({ ...r, order_number: orderMap.get(r.order_id) ?? null })),
      total: count ?? 0,
    };
  });

/** All payouts for the signed-in partner. */
export const listMyPayouts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as any;
    const partner = await ctx.supabase
      .from("referral_partners")
      .select("id")
      .eq("user_id", ctx.userId)
      .maybeSingle();
    if (!partner.data) return { rows: [] };
    const { data, error } = await ctx.supabase
      .from("referral_payouts")
      .select("*")
      .eq("partner_id", partner.data.id)
      .order("requested_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { rows: data ?? [] };
  });

/**
 * Partner requests a payout of their currently-approved commission balance.
 * Loads the caller under RLS, then uses the admin client to atomically insert
 * the payout row and link the approved (unpaid) conversions to it.
 */
export const requestMyPayout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as any;

    const [partnerRes, settingsRes] = await Promise.all([
      ctx.supabase.from("referral_partners").select("*").eq("user_id", ctx.userId).maybeSingle(),
      ctx.supabase
        .from("referral_settings")
        .select("min_payout_cents, payout_fee_cents")
        .eq("id", true)
        .maybeSingle(),
    ]);
    const partner = partnerRes.data;
    if (!partner) throw new Error("You are not a partner yet.");
    if (partner.status !== "approved") throw new Error("Your account is not approved for payouts.");
    if (!partner.payout_method) throw new Error("Add a payout method before requesting a payout.");

    const minPayout = Number(settingsRes.data?.min_payout_cents ?? 5000);
    const feeCents = Number(settingsRes.data?.payout_fee_cents ?? 200);

    const { data: eligible, error: eligErr } = await ctx.supabase
      .from("referral_conversions")
      .select("id, commission_cents")
      .eq("partner_id", partner.id)
      .eq("status", "approved")
      .is("paid_payout_id", null);
    if (eligErr) throw new Error(eligErr.message);
    const rows = eligible ?? [];
    const amount = rows.reduce((s: number, r: any) => s + Number(r.commission_cents ?? 0), 0);
    if (amount < minPayout) {
      throw new Error(
        `Minimum payout is €${(minPayout / 100).toFixed(2)}. Your available balance is €${(amount / 100).toFixed(2)}.`,
      );
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const netCents = Math.max(amount - feeCents, 0);
    const { data: payout, error: payErr } = await supabaseAdmin
      .from("referral_payouts")
      .insert({
        partner_id: partner.id,
        amount_cents: amount,
        fee_cents: feeCents,
        net_cents: netCents,
        currency: "EUR",
        status: "requested",
        method: partner.payout_method,
      })
      .select("*")
      .single();
    if (payErr || !payout) {
      console.error("requestMyPayout insert", payErr);
      throw new Error("Unable to create payout request.");
    }
    const { error: linkErr } = await supabaseAdmin
      .from("referral_conversions")
      .update({ paid_payout_id: payout.id })
      .in(
        "id",
        rows.map((r: any) => r.id),
      );
    if (linkErr) {
      console.error("requestMyPayout link", linkErr);
      await supabaseAdmin.from("referral_payouts").update({ status: "cancelled" }).eq("id", payout.id);
      throw new Error("Unable to reserve conversions for payout. Please try again.");
    }
    return { payout };
  });

